import React, { useState, useContext, useReducer, useRef } from "react";
import Grid from "@mui/material/Grid";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { Box, Typography } from "@mui/material";

import useAccountSettings from "../ManageAccount/useAccountSettings";
import * as api from "../services";
import AppContext from "../appContext";
import { BoardsProvider } from "./boardsContext";
import MutationMessage from "../Components/MutationMessage";
import listReducer from "../Analyser/listReducer";
import AddGraphButton from "./AddGraphButton";
import ImportExport from "./ImportExport";
import Title from "./Title";
import MoodboardList from "./MoodboardList";
import Disclaimer from "../Analyser/Disclaimer";
import useTimeline from "../UserTimeline/useTimeline";
import Alias from "../User/Alias";
import moment from "moment";

export default function MoodBoards() {
  const { t } = useTranslation();
  // client
  const {
    app: {
      client: { id: clientId },
      project: { id: projectId },
      sessionId,
    },
  } = useContext(AppContext);
  const [mutationMessage, setMutationMessage] = useState({
    loading: "loading",
    success: "done",
  });
  const [moodboards, moodboardDispatch] = useReducer(listReducer, undefined);
  const [activeBoardId, setActiveBoardId] = useState(0);
  const { accountSettings } = useAccountSettings();
  const isLocalMoodboard = (id) => ["local", 0].some((b) => b === id);

  // server
  const { timeline } = useTimeline();
  const mutation = useMutation(
    (boards) =>
      api.postLocalMoodboard({
        clientId,
        moodBoardJSON: JSON.stringify(boards),
        projectId,
        sessionId,
      }),
    {
      onSuccess: (data, boards) => {
        if (data.data?.status > 0) {
          moodboardDispatch({
            type: "SET_MOODBOARDS",
            payload: boards,
          });
        }
      },
    } // trigger the old data to be updated
  );

  const removeBoard = (boardId) => {
    const boardsToRemove = listReducer(moodboards, {
      type: "REMOVE_MOODBOARD",
      payload: { id: boardId },
    });
    setMutationMessage({
      loading: t("common.notifications.removeLoading"),
      success: t("common.notifications.removeSuccess"),
    });
    mutation.mutate(boardsToRemove);
  };

  const updateBoard = ({ id, title }) => {
    const boardsToUpdate = listReducer(moodboards, {
      type: "UPDATE_MOODBOARD",
      payload: { id, title },
    });
    setMutationMessage({
      loading: t("common.notifications.renameLoading"),
      success: t("common.notifications.renameSuccess"),
    });
    mutation.mutate(boardsToUpdate);
  };

  const handleActiveBoardId = (id) => setActiveBoardId(id);

  // Render the timeline
  if (timeline?.status === 0 || timeline?.status === -1)
    return <Typography>{t("common.noAccess")}</Typography>;
  return (
    <React.Fragment>
      <Disclaimer />
      <MutationMessage
        mutation={mutation}
        text={{
          loading: mutationMessage.loading,
          success: mutationMessage.success,
        }}
      />
      <BoardsProvider
        value={{
          removeBoard,
          updateBoard,
          moodboardDispatch,
          moodboards,
          activeBoardId,
          isLocalMoodboard,
        }}
      >
        <Title
          activeBoardId={activeBoardId}
          handleActiveBoardId={handleActiveBoardId}
        />
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          spacing={3}
          id="pdf-content"
        >
          <MoodboardList />
          {isLocalMoodboard(activeBoardId) ? (
            <Grid item xs={5} data-html2canvas-ignore="true">
              <AddGraphButton />
            </Grid>
          ) : null}
        </Grid>
        {isLocalMoodboard(activeBoardId) &&
        accountSettings.moodBoardLibrarySave ? (
          <ImportExport />
        ) : null}
      </BoardsProvider>
      <Box padding={6} />
      {/** Client header for printing to PDF */}
      <Box
        alignItems={"center"}
        position={"absolute"}
        id="pdfMoodboardHeader"
        width={"1000px"}
        height={"100px"}
        top={-100000} // hide the print header from the view
      >
        <Alias />
        <Box padding={1} />
        <Typography variant="body2">
          {moment().format("DD/MM/YYYY HH:mm")}
        </Typography>
      </Box>
    </React.Fragment>
  );
}
